<template>
  <LoadingBlock :loading="loading">
    <BaseModal ref="linkModal" :modal-title="linkModalTitle">
      <form @submit.prevent="submitLink">
        <div class="mb-3">
          <label class="form-label" for="ozon-certificate-id">ID сертификата в Ozon</label>
          <input id="ozon-certificate-id" type="number" min="1" step="1" class="form-control"
                 v-model="linkForm.ozon_certificate_id" placeholder="Например, 123456"
                 :disabled="linkLoading">
          <div class="form-text">
            Целое число больше нуля — идентификатор сертификата
            <template v-if="linkForm.number">для «{{ linkForm.number }}»</template>
            в личном кабинете Ozon.
          </div>
        </div>

        <div v-if="linkIdChanged" class="alert alert-warning">
          Вы меняете id на другой. Закэшированный статус модерации будет обнулён, а все записи о привязанных
          товарах — удалены: они относились к прежнему сертификату Ozon. После сохранения нужно заново запустить
          привязку товаров.
        </div>

        <div v-if="linkError" class="alert alert-danger">{{ linkError }}</div>

        <button type="submit" class="btn btn-primary" :disabled="linkLoading">Сохранить</button>
      </form>
    </BaseModal>

    <div class="card mb-4">
      <div class="card-body">
        <div class="row gy-3">
          <div class="col-md-3">
            <input type="text" class="form-control" placeholder="Номер" v-model="filters.number" @change="search">
          </div>
          <div class="col-md-3">
            <Select v-model="filters.kind" :options="kindOptions" optionLabel="name" optionValue="code"
                    placeholder="Вид" showClear style="width: 100%" @change="search"/>
          </div>
          <div class="col-md-3">
            <Select v-model="filters.compliance_type" :options="complianceOptions" optionLabel="name"
                    optionValue="code" placeholder="Тип соответствия" showClear style="width: 100%"
                    @change="search"/>
          </div>
          <div class="col-md-3">
            <Select v-model="filters.uploaded" :options="uploadedOptions" optionLabel="name" optionValue="code"
                    placeholder="Статус выгрузки" showClear style="width: 100%" @change="search"/>
          </div>
          <div class="col-md-3">
            <Select v-model="filters.status_code" :options="ozonStatusOptions" optionLabel="name" optionValue="code"
                    placeholder="Статус Ozon" showClear style="width: 100%" @change="search"/>
          </div>
          <div class="col-md-3">
            <input type="text" class="form-control" placeholder="Бренд" v-model="filters.brand" @change="search">
          </div>
          <div class="col-md-3">
            <input type="text" class="form-control" placeholder="Артикул" v-model="filters.article" @change="search">
          </div>
          <div class="col-md-3">
            <input type="text" class="form-control" placeholder="ТНВЭД" v-model="filters.tnved" @change="search">
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <div class="d-flex align-items-center justify-content-between">
          <h6 class="mb-0">Массовые действия</h6>
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="bulkActionsOpen = !bulkActionsOpen">
            {{ bulkActionsOpen ? 'Скрыть' : 'Показать' }}
          </button>
        </div>
        <div v-if="bulkActionsOpen" class="row gy-3 mt-2">
          <div class="col-md-3 d-flex align-items-end">
            <div class="form-check mb-2">
              <input class="form-check-input" type="checkbox" id="ozon-only-active" v-model="bulkOnlyActive">
              <label class="form-check-label" for="ozon-only-active">Только активные</label>
            </div>
          </div>
          <div class="col-md-3">
            <label class="form-label">Статус для sync</label>
            <Select v-model="syncFilters.status" :options="ozonStatusOptions" optionLabel="name" optionValue="code"
                    placeholder="Любой статус" showClear style="width: 100%"/>
          </div>
          <div class="col-md-3">
            <label class="form-label">Тип для sync</label>
            <Select v-model="syncFilters.type" :options="ozonSyncTypeOptions" optionLabel="name" optionValue="code"
                    placeholder="Любой тип" showClear style="width: 100%"/>
          </div>
          <div class="col-md-3">
            <label class="form-label">Offer ID для sync</label>
            <input type="text" class="form-control" v-model="syncFilters.offer_id" placeholder="Необязательно">
          </div>
          <div class="col-12">
            <div class="demo-inline-spacing">
              <button type="button" class="btn btn-outline-primary" @click="uploadNew">
                Выгрузить новые
              </button>
              <button type="button" class="btn btn-outline-primary" @click="bindAll">
                Привязать товары к выгруженным сертификатам
              </button>
              <button type="button" class="btn btn-outline-secondary" @click="syncCertificates">
                Обновить статусы сертификатов
              </button>
              <button type="button" class="btn btn-outline-secondary" @click="syncAllProducts">
                Обновить статусы товаров всех сертификатов
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div v-if="!items.length" class="text-muted mb-3">Сертификаты не найдены</div>

        <div class="table-responsive text-nowrap">
          <table class="table">
            <thead>
            <tr>
              <th>Номер</th>
              <th>Вид</th>
              <th>Тип</th>
              <th>Даты</th>
              <th>Бренды</th>
              <th>Ozon</th>
              <th>Товары</th>
              <th>Документ</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="item in items" :key="item.id">
              <td>
                <router-link :to="{ name: 'certificate-detail', params: { id: item.id } }">
                  {{ item.number }}
                </router-link>
              </td>
              <td>{{ getKindLabel(item.kind) }}</td>
              <td>{{ getComplianceLabel(item.compliance_type) }}</td>
              <td>
                {{ formatCertificateDate(item.issue_date) }}<br>
                <small>{{ item.is_perpetual ? 'Бессрочный' : formatCertificateDate(item.valid_until) }}</small>
              </td>
              <td>{{ formatTags(item.brands) }}</td>
              <td>
                <template v-if="item.ozon">
                  <div>{{ getOzonCertificateStatusLabel(item.ozon.status_code) || 'Статус не проверен' }}</div>
                  <small>ID: {{ item.ozon.ozon_certificate_id || '—' }}</small>
                  <div v-if="item.ozon.verification_comment" class="text-muted">
                    {{ item.ozon.verification_comment }}
                  </div>
                </template>
                <span v-else class="text-muted">Не выгружен</span>
              </td>
              <td>{{ item.ozon?.products_count || 0 }}</td>
              <td>
                <a v-if="item.document?.file_url" :href="item.document.file_url" target="_blank"
                   rel="noopener noreferrer">
                  {{ item.document.file_name || 'Скачать' }}
                </a>
                <span v-else>—</span>
              </td>
              <td class="text-end">
                <div class="demo-inline-spacing">
                  <button type="button" class="btn btn-sm btn-outline-primary" @click="uploadOne(item.id)">
                    Выгрузить
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-primary" @click="bindOne(item.id)">
                    Привязать товары
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-secondary" @click="syncOneProducts(item.id)">
                    Обновить товары
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-secondary" @click="openLinkModal(item)">
                    {{ item.ozon ? 'Изменить id' : 'Указать id вручную' }}
                  </button>
                  <button v-if="item.ozon" type="button" class="btn btn-sm btn-outline-danger"
                          @click="unlinkOne(item)">
                    Удалить связь
                  </button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <BasePagination :info="paginationInfo" @changePage="changePage"/>
      </div>
    </div>
  </LoadingBlock>
</template>

<script>
import LoadingBlock from "@/components/page/LoadingBlock.vue";
import BasePagination from "@/components/list/Pagination.vue";
import BaseModal from "@/components/Modal.vue";
import {Select} from "primevue";
import {
    bindAllOzonCertificates,
    bindOzonCertificateProducts,
    getOzonCertificates,
    linkOzonCertificate,
    syncAllOzonCertificateProducts,
    syncOzonCertificateProducts,
    syncOzonCertificates,
    unlinkOzonCertificate,
    uploadNewOzonCertificates,
    uploadOzonCertificate,
} from "@/api/certificate";
import {
    certificateKinds,
    complianceTypes,
    formatCertificateDate,
    formatTags,
    getComplianceLabel,
    getKindLabel,
    getOzonCertificateStatusLabel,
    ozonCertificateStatuses,
    ozonSyncTypes,
    ozonUploadStatuses,
    parseCertificateError,
    parseOzonError,
} from "@/data/certificate";
import {afterStoreAction} from "@/helpers/store";
import {Message, Messages} from "@/data/messages";

export default {
  name: "CertificateOzonCertificates",
  components: {LoadingBlock, BasePagination, BaseModal, Select},
  props: {
    storeId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      items: [],
      paginationInfo: {},
      filters: {
        page: 1,
        size: 50,
        kind: null,
        compliance_type: null,
        number: null,
        brand: null,
        article: null,
        tnved: null,
        uploaded: null,
        status_code: null,
      },
      syncFilters: {
        status: null,
        type: null,
        offer_id: null,
      },
      bulkOnlyActive: true,
      bulkActionsOpen: false,
      linkForm: {
        certificate_id: null,
        number: '',
        current_ozon_certificate_id: null,
        ozon_certificate_id: '',
      },
      linkLoading: false,
      linkError: null,
      kindOptions: certificateKinds,
      complianceOptions: complianceTypes,
      uploadedOptions: ozonUploadStatuses,
      ozonStatusOptions: ozonCertificateStatuses,
      ozonSyncTypeOptions: ozonSyncTypes,
    }
  },
  computed: {
    linkModalTitle() {
      return this.linkForm.current_ozon_certificate_id
          ? 'Изменить id сертификата в Ozon'
          : 'Указать id сертификата в Ozon'
    },
    linkIdChanged() {
      const current = Number(this.linkForm.current_ozon_certificate_id)
      const next = Number(this.linkForm.ozon_certificate_id)
      return !!current && next > 0 && next !== current
    },
  },
  methods: {
    getKindLabel,
    getComplianceLabel,
    getOzonCertificateStatusLabel,
    formatCertificateDate,
    formatTags,
    buildFilters() {
      return {...this.filters}
    },
    buildBulkFilters() {
      return {
        kind: this.filters.kind || null,
        brand: this.filters.brand || null,
        article: this.filters.article || null,
        tnved: this.filters.tnved || null,
        only_active: this.bulkOnlyActive,
      }
    },
    async load() {
      this.loading = true
      try {
        const result = await getOzonCertificates(this.storeId, this.buildFilters())
        this.items = result.data.items || []
        this.paginationInfo = {
          total: result.data.total,
          pages: result.data.pages,
          page: result.data.page,
          size: result.data.size,
        }
      } catch (e) {
        this.showError(e)
      }
      this.loading = false
    },
    async search() {
      this.filters.page = 1
      await this.load()
    },
    async changePage(page) {
      this.filters.page = page
      await this.load()
    },
    async runAction(action) {
      this.loading = true
      try {
        const result = await action()
        afterStoreAction(result)
        await this.load()
      } catch (e) {
        this.showError(e)
      }
      this.loading = false
    },
    async uploadOne(certificateId) {
      await this.runAction(() => uploadOzonCertificate(this.storeId, certificateId))
    },
    async bindOne(certificateId) {
      await this.runAction(() => bindOzonCertificateProducts(this.storeId, certificateId, null))
    },
    async syncOneProducts(certificateId) {
      await this.runAction(() => syncOzonCertificateProducts(this.storeId, certificateId, null))
    },
    async uploadNew() {
      await this.runAction(() => uploadNewOzonCertificates(this.storeId, this.buildBulkFilters()))
    },
    async bindAll() {
      await this.runAction(() => bindAllOzonCertificates(this.storeId, this.buildBulkFilters()))
    },
    async syncCertificates() {
      await this.runAction(() => syncOzonCertificates(this.storeId, {...this.syncFilters}))
    },
    async syncAllProducts() {
      await this.runAction(() => syncAllOzonCertificateProducts(this.storeId, null))
    },
    openLinkModal(item) {
      const current = item.ozon?.ozon_certificate_id || null
      this.linkForm = {
        certificate_id: item.id,
        number: item.number,
        current_ozon_certificate_id: current,
        ozon_certificate_id: current ? String(current) : '',
      }
      this.linkError = null
      this.$refs.linkModal.openModal()
    },
    async submitLink() {
      const ozonCertificateId = Number(this.linkForm.ozon_certificate_id)
      if (!Number.isInteger(ozonCertificateId) || ozonCertificateId <= 0) {
        this.linkError = 'Укажите целое число больше нуля'
        return
      }

      const wasChanged = this.linkIdChanged
      this.linkError = null
      this.linkLoading = true
      try {
        await linkOzonCertificate(this.storeId, this.linkForm.certificate_id, ozonCertificateId)
        this.$refs.linkModal.closeModal()
        this.showSuccess(wasChanged
            ? `Id сертификата Ozon изменён на ${ozonCertificateId}. Статус модерации и привязанные товары сброшены — запустите привязку товаров заново.`
            : `Сертификат привязан к id Ozon ${ozonCertificateId}`)
        await this.load()
      } catch (e) {
        this.linkError = parseOzonError(e)
      }
      this.linkLoading = false
    },
    async unlinkOne(item) {
      const current = item.ozon?.ozon_certificate_id || '—'
      if (!confirm(`Удалить связь сертификата «${item.number}» с id Ozon ${current}? Статус модерации и записи о привязанных товарах будут удалены.`)) {
        return
      }
      this.loading = true
      try {
        await unlinkOzonCertificate(this.storeId, item.id)
        this.showSuccess('Связь с сертификатом Ozon удалена')
        await this.load()
      } catch (e) {
        this.showOzonError(e)
      }
      this.loading = false
    },
    showSuccess(text) {
      const message = new Message()
      message.type = 'success'
      message.text = text
      message.autoremove = 10
      Messages.list.push(message)
    },
    showError(e) {
      const message = new Message()
      message.type = 'failure'
      message.text = parseCertificateError(e)
      Messages.list.push(message)
    },
    showOzonError(e) {
      const message = new Message()
      message.type = 'failure'
      message.text = parseOzonError(e)
      Messages.list.push(message)
    },
  },
  async mounted() {
    await this.load()
  },
  watch: {
    storeId() {
      this.filters.page = 1
      this.load()
    },
  },
}
</script>
